import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import PostCard from "../components/PostCard";
import VerifiedBadge from "../components/VerifiedBadge";
import PitchHeatmap, { resolvePosition } from "../components/PitchHeatmap";
import FitnessTrends from "../components/FitnessTrends";
import VerificationCard from "../components/VerificationCard";
import { useAuth } from "../context/AuthContext";
import EditProfileDialog from "../components/EditProfileDialog";
import ShareCardDialog from "../components/ShareCardDialog";
import ShortlistButton from "../components/ShortlistButton";
import { ReportDialog } from "../components/ReportDialog";
import { MessageSquare, ArrowLeftRight, Flag, Ban, ImageDown } from "lucide-react";
import { toast } from "sonner";

const ACCOUNT_CHIPS = {
  parent_run: "Parent-managed",
  youth_13_15: "Youth account",
  youth_16_17: "Youth account",
};

export const AVAILABILITY_META = {
  green: { label: "Looking for a club", dot: "bg-green-500", cls: "border-green-500/60 text-green-400 bg-black/90" },
  yellow: { label: "Open to offers", dot: "bg-yellow-400", cls: "border-yellow-400/60 text-yellow-300 bg-black/90" },
  red: { label: "Not looking", dot: "bg-red-500", cls: "border-red-500/60 text-red-400 bg-black/90" },
};

export default function ProfilePage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user: me, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ career: { goals: 0, assists: 0, minutes: 0, matches: 0 }, seasons: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shortlists, setShortlists] = useState([]);

  const isScout = me?.role === "scout" || me?.role === "coach";

  useEffect(() => {
    if (!isScout) return;
    api.get("/shortlists").then(({ data }) => setShortlists(data)).catch(() => {});
  }, [isScout]);

  const toggleBlock = async () => {
    try {
      if (profile.is_blocked) {
        await api.post(`/users/${profile.id}/unblock`);
        toast.success(`${profile.name} unblocked`);
      } else {
        await api.post(`/users/${profile.id}/block`);
        toast.success(`${profile.name} blocked — they can no longer message or follow you`);
      }
      load();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update block");
    }
  };

  const load = async () => {
    const { data } = await api.get(`/users/${userId}`);
    setProfile(data);
    const [{ data: ps }, { data: st }] = await Promise.all([
      api.get(`/users/${userId}/posts`),
      api.get(`/users/${userId}/stats`),
    ]);
    setPosts(ps);
    setStats(st);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const toggleFollow = async () => {
    if (!me) return;
    if (profile.is_following) {
      await api.post(`/users/${profile.id}/unfollow`);
    } else {
      await api.post(`/users/${profile.id}/follow`);
    }
    load();
    refresh();
  };

  const startConversation = async () => {
    try {
      const { data } = await api.post("/conversations", { user_id: profile.id });
      nav(`/messages/${data.id}`);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === "message_request_required") {
        try {
          const { data } = await api.post("/message-requests", { user_id: profile.id });
          toast.success(
            data.already_requested
              ? "Request already pending — the player will decide"
              : "This youth player only takes direct messages from verified scouts. Message request sent for their approval."
          );
        } catch (reqErr) {
          toast.error(reqErr.response?.data?.detail || "Could not send request");
        }
      } else {
        toast.error(err.response?.data?.detail || "Could not start conversation");
      }
    }
  };

  if (!profile) {
    return <div className="max-w-4xl mx-auto p-8 text-[#737373]">Loading…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="profile-page">
      <div className="bg-[#121212] border border-[#262626] rounded-sm overflow-hidden">
        <div
          className="h-40 md:h-56 bg-cover bg-center"
          style={{
            backgroundImage: profile.cover_url
              ? `url(${profile.cover_url})`
              : `url(https://images.pexels.com/photos/28827841/pexels-photo-28827841.jpeg)`,
          }}
        />
        <div className="px-6 pb-6 -mt-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="flex items-end gap-4">
              <div className="ring-4 ring-[#050505] rounded-full">
                <Avatar url={profile.avatar_url} name={profile.name} size={96} />
              </div>
              <div className="pb-2">
                <h1 data-testid="profile-name" className="font-display text-3xl md:text-4xl uppercase tracking-tight text-white leading-none flex items-center gap-2 flex-wrap">
                  {profile.name}
                  <VerifiedBadge role={profile.role} verified={profile.verified} size={22} showLabel />
                  {ACCOUNT_CHIPS[profile.account_type] && (
                    <span
                      data-testid="profile-account-chip"
                      className="text-[10px] uppercase tracking-widest text-white bg-black/90 border border-[#525252] px-2 py-1 rounded-sm font-body font-normal"
                    >
                      {ACCOUNT_CHIPS[profile.account_type]}
                    </span>
                  )}
                  {AVAILABILITY_META[profile.availability] && (
                    <span
                      data-testid="profile-availability"
                      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm font-body font-normal border ${AVAILABILITY_META[profile.availability].cls}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${AVAILABILITY_META[profile.availability].dot} animate-pulse`} />
                      {AVAILABILITY_META[profile.availability].label}
                    </span>
                  )}
                </h1>
                <div className="text-sm text-[#a3a3a3] mt-1">
                  {profile.role && profile.role !== "player"
                    ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
                    : profile.position || "Player"}
                  {profile.team ? (
                    <>
                      {" · "}
                      <Link
                        to={`/team/${encodeURIComponent(profile.team)}`}
                        data-testid="profile-team-link"
                        className="text-white hover:underline"
                      >
                        {profile.team}
                      </Link>
                    </>
                  ) : null}
                  {(profile.city || profile.country) && (
                    <span data-testid="profile-location">
                      {" · "}
                      {[profile.city, profile.country].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {profile.region && (
                    <span data-testid="profile-region">
                      {" · "}
                      {profile.region}
                    </span>
                  )}
                </div>
                {(profile.age || profile.height_cm || profile.preferred_foot || profile.level_of_play) && (
                  <div data-testid="profile-attributes" className="flex flex-wrap gap-2 mt-2">
                    {profile.age ? <AttrChip testid="profile-age" label={`Age ${profile.age}`} /> : null}
                    {profile.height_cm ? <AttrChip testid="profile-height" label={`${profile.height_cm} cm`} /> : null}
                    {profile.preferred_foot ? (
                      <AttrChip
                        testid="profile-foot"
                        label={`${profile.preferred_foot.charAt(0).toUpperCase() + profile.preferred_foot.slice(1)} foot`}
                      />
                    ) : null}
                    {profile.level_of_play ? (
                      <AttrChip
                        testid="profile-level"
                        label={profile.level_of_play.charAt(0).toUpperCase() + profile.level_of_play.slice(1)}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <div className="pb-2 flex gap-2">
              {profile.is_me ? (
                <>
                  {profile.role === "player" && (
                    <button
                      data-testid="profile-share-card-btn"
                      onClick={() => setShareOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold hover:bg-gray-200 rounded-sm transition-colors text-sm"
                    >
                      <ImageDown className="w-4 h-4" />
                      Share card
                    </button>
                  )}
                  <button
                    data-testid="profile-edit-btn"
                    onClick={() => setEditOpen(true)}
                    className="px-4 py-2 border border-[#404040] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors text-sm"
                  >
                    Edit profile
                  </button>
                </>
              ) : me ? (
                <>
                  {isScout && profile.role === "player" && (
                    <ShortlistButton playerId={profile.id} shortlists={shortlists} onChange={setShortlists} />
                  )}
                  <Link
                    to={`/compare?a=${profile.id}`}
                    data-testid="profile-compare-btn"
                    className="flex items-center gap-2 px-4 py-2 border border-[#404040] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors text-sm"
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                    Compare
                  </Link>
                  <button
                    data-testid="profile-message-btn"
                    onClick={startConversation}
                    className="flex items-center gap-2 px-4 py-2 border border-[#404040] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors text-sm"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Message
                  </button>
                  <button
                    data-testid="profile-follow-btn"
                    onClick={toggleFollow}
                    className={`px-6 py-2 rounded-sm text-sm font-semibold transition-colors ${
                      profile.is_following
                        ? "bg-transparent border border-[#404040] text-white hover:bg-[#1a1a1a]"
                        : "bg-white text-black hover:bg-gray-200"
                    }`}
                  >
                    {profile.is_following ? "Following" : "Follow"}
                  </button>
                  <button
                    data-testid="profile-report-btn"
                    onClick={() => setReportOpen(true)}
                    title="Report this profile"
                    className="px-3 py-2 border border-[#404040] text-[#a3a3a3] hover:text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
                  >
                    <Flag className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="profile-block-btn"
                    onClick={toggleBlock}
                    title={profile.is_blocked ? "Unblock" : "Block this user"}
                    className={`px-3 py-2 border rounded-sm transition-colors ${
                      profile.is_blocked
                        ? "border-red-500/60 text-red-400 hover:bg-red-500/10"
                        : "border-[#404040] text-[#a3a3a3] hover:text-white hover:bg-[#1a1a1a]"
                    }`}
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                </>
              ) : null}
            </div>
          </div>
          {profile.bio && (
            <p className="mt-4 text-[#e5e5e5] max-w-2xl whitespace-pre-wrap text-sm leading-relaxed">
              {profile.bio}
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-6">
            <StatCell label="Followers" value={profile.followers_count} testid="profile-followers-count" />
            <StatCell label="Following" value={profile.following_count} testid="profile-following-count" />
            <StatCell label="Matches" value={stats.career.matches} testid="profile-total-matches" />
            <StatCell label="Goals" value={stats.career.goals} testid="profile-total-goals" />
            <StatCell label="Assists" value={stats.career.assists} testid="profile-total-assists" />
            <StatCell label="Mins" value={stats.career.minutes} testid="profile-total-mins" />
          </div>
        </div>
      </div>

      {me?.id === profile.id && (profile.role === "scout" || profile.role === "coach") && !profile.verified && (
        <VerificationCard />
      )}

      {resolvePosition(profile.position) && (
        <div
          className="bg-[#121212] border border-[#262626] rounded-sm p-5 flex items-center gap-6"
          data-testid="position-map-section"
        >
          <div className="w-28 sm:w-32 shrink-0">
            <PitchHeatmap position={profile.position} />
          </div>
          <div>
            <h2 className="font-display text-xl uppercase tracking-widest text-white">
              Position Map
            </h2>
            <div className="font-display text-3xl text-white mt-2 leading-none">
              {resolvePosition(profile.position)}
            </div>
            <p className="text-xs text-[#a3a3a3] mt-2 max-w-xs">
              Primary operating zone based on {profile.name?.split(" ")[0]}'s registered position
              ({profile.position}).
            </p>
          </div>
        </div>
      )}

      {stats.seasons.length > 0 && (
        <div className="bg-[#121212] border border-[#262626] rounded-sm p-5" data-testid="season-stats-section">
          <h2 className="font-display text-xl uppercase tracking-widest text-white mb-4">
            Season Stats
          </h2>
          <div className="space-y-2">
            <div className="grid grid-cols-5 gap-2 text-[10px] uppercase tracking-widest text-[#737373] px-3">
              <div>Season</div>
              <div className="text-center">Matches</div>
              <div className="text-center">Goals</div>
              <div className="text-center">Assists</div>
              <div className="text-center">Mins</div>
            </div>
            {stats.seasons.map((s) => (
              <div
                key={s.season}
                data-testid={`season-row-${s.season.replace("/", "-")}`}
                className="grid grid-cols-5 gap-2 items-center bg-[#0a0a0a] border border-[#262626] px-3 py-3 rounded-sm"
              >
                <div className="font-display text-lg text-white leading-none">{s.season}</div>
                <div className="text-center font-display text-lg text-white">{s.matches}</div>
                <div className="text-center font-display text-lg text-white">{s.goals}</div>
                <div className="text-center font-display text-lg text-white">{s.assists}</div>
                <div className="text-center font-display text-lg text-white">{s.minutes}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <FitnessTrends userId={userId} />

      <h2 className="font-display text-xl uppercase tracking-widest text-white pt-2">Match Log</h2>
      {posts.length === 0 ? (
        <div className="bg-[#121212] border border-[#262626] p-8 text-center rounded-sm text-[#a3a3a3]">
          No matches posted yet.
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} onDelete={(id) => setPosts((ps) => ps.filter((x) => x.id !== id))} />
          ))}
        </div>
      )}

      {editOpen && (
        <EditProfileDialog
          profile={profile}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); refresh(); }}
        />
      )}
      {reportOpen && (
        <ReportDialog
          targetType="user"
          targetId={profile.id}
          targetLabel={profile.name}
          onClose={() => setReportOpen(false)}
        />
      )}
      {shareOpen && (
        <ShareCardDialog
          profile={profile}
          career={stats.career}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#262626] p-3 text-center rounded-sm">
      <div data-testid={testid} className="font-display text-2xl text-white leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{label}</div>
    </div>
  );
}

function AttrChip({ label, testid }) {
  return (
    <span
      data-testid={testid}
      className="text-[10px] uppercase tracking-widest text-white bg-black/90 border border-[#404040] px-2 py-1 rounded-sm"
    >
      {label}
    </span>
  );
}
